import './UmpireStrikeZoneAccuracyChart.scss';

import { useTranslation } from 'react-i18next';

import {
  UmpireStrikeZoneAccuracyChartConfigDto,
  UmpireStrikeZoneAccuracyChartDataDto,
  UmpireStrikeZoneAccuracyDataDto,
} from 'types/CodegenReportTypes';

import { ChartProps } from 'utilities/chartIdToComponent';
import ProgressBar from 'components/Basic/ProgressBar/ProgressBar';
import { Typography } from '@trackman/web-shared-components';
import { useCallback } from 'react';
import { useChartDataDisplay } from 'hooks/helperHooks';
import { getOrderedColorSet } from 'utilities/colorHelpers';

interface UmpireStrikeZoneMetric {
  data: UmpireStrikeZoneAccuracyDataDto | undefined;
  label: React.ReactNode;
}

const UmpireStrikeZoneMetricDisplay: React.FC<UmpireStrikeZoneMetric> = ({ data, label }) => {
  return (
    <div className='UmpireStrikeZoneAccuracyChart-metric'>
      {data == null || data.total == null || data.total === 0 ? (
        <div>
          <Typography variant='h1' component='span'>
            -
          </Typography>
          <Typography component='span'> = - / -</Typography>
        </div>
      ) : (
        <div>
          <Typography variant='h1' component='span'>
            {Math.round(data.percentage)} %
          </Typography>
          <Typography component='span'>{` = ${data.score} / ${data.total}`}</Typography>
        </div>
      )}
      <Typography className='UmpireStrikeZoneAccuracyChart-metric-label' component='div'>
        {label}
      </Typography>
      <ProgressBar percentile={data?.percentage ?? 0} backgroundColor={getOrderedColorSet(0, 100, data?.percentage ?? 0).bg} />
    </div>
  );
};

const UmpireStrikeZoneAccuracyChart = ({
  chartData,
  isLoading,
  chartConfig,
}: ChartProps<UmpireStrikeZoneAccuracyChartDataDto, UmpireStrikeZoneAccuracyChartConfigDto>) => {
  const { t } = useTranslation();

  const simulateChartData = useCallback((isLoading: boolean) => {
    function getRandomAccuracy(isLoading: boolean): UmpireStrikeZoneAccuracyDataDto | undefined {
      if (!isLoading) {
        return undefined;
      }

      const random = Math.floor(Math.random() * 100);
      return { score: random, total: 100, percentage: random };
    }

    return {
      overallAccuracy: getRandomAccuracy(isLoading),
      calledBallAccuracy: getRandomAccuracy(isLoading),
      calledStrikeAccuracy: getRandomAccuracy(isLoading),
      bufferZoneAccuracy: getRandomAccuracy(isLoading),
    };
  }, []);

  const chartDataDisplay = useChartDataDisplay(chartData, simulateChartData, isLoading);

  return (
    <div className='UmpireStrikeZoneAccuracyChart-container'>
      <UmpireStrikeZoneMetricDisplay
        data={chartDataDisplay.overallAccuracy}
        label={t('UmpireStrikeZoneAccuracyChart.OverallCorrectCalls')}
      />
      <UmpireStrikeZoneMetricDisplay
        data={chartDataDisplay.calledBallAccuracy}
        label={
          <>
            {t('UmpireStrikeZoneAccuracyChart.CalledBallsThatWere')} <b>{t('UmpireStrikeZoneAccuracyChart.TrueBalls')}</b>
          </>
        }
      />
      <UmpireStrikeZoneMetricDisplay
        data={chartDataDisplay.calledStrikeAccuracy}
        label={
          <>
            {t('UmpireStrikeZoneAccuracyChart.CalledStrikesThatWere')} <b>{t('UmpireStrikeZoneAccuracyChart.TrueStrikes')}</b>
          </>
        }
      />
      <UmpireStrikeZoneMetricDisplay
        data={chartDataDisplay.bufferZoneAccuracy}
        label={t('UmpireStrikeZoneAccuracyChart.CalledStrikesInBufferZone')}
      />
    </div>
  );
};

export default UmpireStrikeZoneAccuracyChart;
